import {
  type OpenChangeReason,
  arrow,
  autoUpdate,
  flip,
  hide,
  inline,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import { type SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import type { PopoverRootPropsInterface } from '../types/popover-root.types';
import type {
  PopoverFloatingContextInterface,
  UsePopoverReturnInterface,
} from '../types/use-popover.types';

export function usePopover(props: Partial<PopoverRootPropsInterface>): {
  popover: UsePopoverReturnInterface;
  floating: PopoverFloatingContextInterface;
} {
  const {
    defaultOpen,
    offset: offsetValue = 22,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    overflowPadding = 16,
    placement = 'top-start',
    shouldCloseOnViewportLeave = false,
    shouldAwaitInteractionResponse = true,
    useClickProps,
    useDismissProps,
    useRoleProps,
  } = props;

  const arrowRef = useRef(null);

  /**
   * Accessibility props and handlers to connect header with body
   */
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = 'open' in props;
  const open = isControlled ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, floatingStyles, middlewareData } = useFloating({
    placement,

    open,

    onOpenChange: async (state, event, reason) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event, reason);
    },

    whileElementsMounted: autoUpdate,

    /**
     * Middleware plugins have to be in certain order.
     * See: https://floating-ui.com/docs/useFloating#middleware
     */
    middleware: [
      // Translates the floating element along the specified axes
      offset(offsetValue),

      // Improves positioning for inline reference elements that span over multiple lines
      inline(),

      // Changes the placement of the floating element to keep it in view
      flip({
        fallbackAxisSideDirection: 'start',
        padding: overflowPadding,
      }),

      // Provides positioning data for an arrow element inside the floating element
      arrow({ element: arrowRef, padding: overflowPadding }),

      // A data provider that allows you to hide the floating element
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */
  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) =>
      !(event.target as HTMLElement).closest('[data-c11n-component="Popover"]'),
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'dialog',
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss, role]);

  /**
   * Motion helpers and styles
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, styles: floatingTransitionStyles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : 250,
    initial: {
      opacity: 0,
      transform: 'scale(0.975)',
    },
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlled) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }
    },
    [isControlled, setControlledOpen],
  );

  /**
   * Handles `Popover.Close` and `Popover.CloseButton` click actions.
   *
   * @param event - Event object
   */
  const onClickClose = useCallback(
    (event: SyntheticEvent) => {
      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState],
  );

  /**
   * Arrow placement data for styling (Floating UI handles positioning)
   */
  const [arrowSide, arrowAlignment] = context.placement.split('-') as [
    'top' | 'bottom' | 'left' | 'right',
    'start' | 'end' | undefined,
  ];

  const popover = useMemo(
    () => ({
      // Mounted state and handlers
      isMounted,
      onClickClose,

      // Refs
      arrowRef,
      refs,

      // Trigger and reference props
      getReferenceProps,

      // Styles
      floatingStyles,
      floatingTransitionStyles,

      // Arrow placement data
      arrowSide,
      arrowAlignment,

      // Accessibility props and handlers
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,

      middlewareData,
    }),
    [
      isMounted,
      onClickClose,
      refs,
      getReferenceProps,
      floatingStyles,
      floatingTransitionStyles,
      arrowSide,
      arrowAlignment,
      labelId,
      descriptionId,
      middlewareData,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * Popover.Content and Popover.Arrow pay for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps };

  return { popover, floating };
}
